import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";

import {
  Autocomplete,
  DirectionsRenderer,
  DirectionsService,
  GoogleMap,
  useJsApiLoader,
} from "@react-google-maps/api";

import { Plus, Trash2 } from "lucide-react";
import { useStatus } from "../../popup/NotificationContext";
import Loader from "../../mainframes/Loader";
import {
  useAddRouteMutation,
  useDeleteRouteMutation,
  useGetRoutesQuery,
  useUpdateRouteMutation,
} from "../../redux/logistics/logisticsApi";
import { useGetAllLiteDevicesQuery, useUpdateDeviceAdvancedSettingsMutation } from "../../redux/admin/adminApi";
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_VERSION } from "../../config/googleMaps";
import { getDeviceStatus as resolveVehicleStatus } from "../../utils/vehicleStatus";
import api from "../../redux/apis/api";
import "./add-route-modal.css";
import FlexibleStatsRow from "../common/FlexibleStatsRow";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const mapContainerStyle = {
  width: "100%",
  height: "100%",
  minHeight: "560px",
  borderRadius: "18px",
};

const defaultCenter = { lat: 20, lng: 0 };

const getDirectionsErrorMessage = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "NOT_FOUND":
      return "Source ya destination locate nahi hua. Address dobara select karo.";
    case "ZERO_RESULTS":
      return "In locations ke liye drivable route nahi mila.";
    case "MAX_WAYPOINTS_EXCEEDED":
      return "Stops zyada hain. Kuch stops hata kar dubara try karo.";
    case "OVER_QUERY_LIMIT":
      return "Route service thodi der ke liye busy hai. 5-10 second baad retry karo.";
    case "REQUEST_DENIED":
      return "Google Maps directions request denied hui. API setup verify karo.";
    case "INVALID_REQUEST":
      return "Route request incomplete hai. Source, destination aur stops verify karo.";
    default:
      return "Route preview load nahi hua. Ek baar locations reselect karke retry karo.";
  }
};

const parseRoutesPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.routes)) return payload.routes;
  return [];
};

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", { hour12: true }).toUpperCase();
};

const getDeviceStatus = (device) => {
  return String(resolveVehicleStatus(device) || "no_data");
};

const getStatusColor = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("running")) return "#10b981";
  if (value.includes("stop")) return "#dc2626";
  if (value.includes("idle")) return "#f59e0b";
  if (value.includes("expired") || value.includes("not reporting") || value.includes("offline")) return "#ef4444";
  return "#ef4444";
};

const kmFromTotalDistance = (value) => {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  const raw = String(value).trim().toLowerCase();
  const numeric = Number(raw.replace(/km|kms|,|\s/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeStops = (stops) => {
  if (!Array.isArray(stops)) return [];
  return stops
    .map((s) => ({
      address: s?.address || s?.location || "",
      lat: s?.lat ?? null,
      lng: s?.lng ?? null,
    }))
    .filter((s) => String(s.address || "").trim());
};

const normalizeMongoId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return String(value?._id || value?.id || "");
  return String(value);
};

const parseLatLngValue = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\s*$/);
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
};

const normalizeRoutePathPoint = (point) => {
  if (!point) return null;
  const latValue =
    typeof point.lat === "function" ? point.lat() : point.lat ?? point.latitude ?? point?.location?.lat;
  const lngValue =
    typeof point.lng === "function" ? point.lng() : point.lng ?? point.longitude ?? point?.location?.lng;
  const lat = Number(latValue);
  const lng = Number(lngValue);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const extractSelectedRoutePath = (route) => {
  const overviewPath = Array.isArray(route?.overview_path) ? route.overview_path : [];
  const normalizedOverview = overviewPath.map(normalizeRoutePathPoint).filter(Boolean);
  if (normalizedOverview.length >= 2) return normalizedOverview;

  const legs = Array.isArray(route?.legs) ? route.legs : [];
  const fromSteps = legs
    .flatMap((leg) => (Array.isArray(leg?.steps) ? leg.steps : []))
    .flatMap((step) => {
      const stepPath = Array.isArray(step?.path) ? step.path : [];
      return stepPath.map(normalizeRoutePathPoint).filter(Boolean);
    });

  return fromSteps.length >= 2 ? fromSteps : [];
};

const normalizeExistingRoutePath = (points) => {
  if (!Array.isArray(points)) return [];
  return points.map(normalizeRoutePathPoint).filter(Boolean);
};

const buildCsv = (rows) => {
  const headers = ["Id", "Route Name", "Source", "Destination", "No of stops", "Route length"];
  const body = rows.map((row) =>
    [row.idLabel, row.routeName, row.source, row.destination, row.stopsCount, row.routeLength]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...body].join("\n");
};

const EmptyState = () => (
  <Stack spacing={1} alignItems="center" sx={{ py: 10, color: "#6b7280" }}>
    <Box
      sx={{
        width: 92,
        height: 92,
        borderRadius: "50%",
        bgcolor: "#fff1f2",
        display: "grid",
        placeItems: "center",
        color: "#fb7185",
        fontSize: 34,
        userSelect: "none",
      }}
    >
      ✕
    </Box>
    <Typography sx={{ fontWeight: 700 }}>No Data Available In Table</Typography>
  </Stack>
);

function RouteForm({
  mode,
  initialRoute,
  devices,
  onCancel,
  onSaved,
  submitLabel = "SUBMIT",
  cancelLabel = "CANCEL",
  compact = false,
}) {
  const { showStatus } = useStatus();
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const [addRoute, { isLoading: adding }] = useAddRouteMutation();
  const [updateRoute, { isLoading: updating }] = useUpdateRouteMutation();
  const [updateAdvancedSettings, { isLoading: assigning }] = useUpdateDeviceAdvancedSettingsMutation();

  const isView = mode === "view";
  const isEdit = mode === "edit";

  const [sourceText, setSourceText] = useState("");
  const [destinationText, setDestinationText] = useState("");
  const [routeName, setRouteName] = useState("");
  const [stops, setStops] = useState([]);
  const [currentStop, setCurrentStop] = useState("");
  const [currentStopResolved, setCurrentStopResolved] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");

  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [shouldRenderRoute, setShouldRenderRoute] = useState(false);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [routeRequestKey, setRouteRequestKey] = useState(0);
  const [isRouteLoading, setIsRouteLoading] = useState(false);

  const sourceRef = useRef(null);
  const destRef = useRef(null);
  const stopRef = useRef(null);
  const lastDirectionsErrorRef = useRef("");

  const resolveTypedLocation = useCallback(
    async (value, mode = "address") => {
      const raw = String(value || "").trim();
      if (!raw) return null;

      const directLatLng = parseLatLngValue(raw);
      if (directLatLng) {
        return {
          address: raw,
          lat: directLatLng.lat,
          lng: directLatLng.lng,
        };
      }

      if (window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        const result = await new Promise((resolve) => {
          geocoder.geocode({ address: raw }, (results = [], status) => {
            if (status !== "OK" || !results[0]?.geometry?.location) {
              resolve(null);
              return;
            }
            const place = results[0];
            resolve({
              address: place.formatted_address || raw,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            });
          });
        });
        if (result) return result;
      }

      try {
        const response = await api.get("/geocode/search", { params: { q: raw } });
        const first = Array.isArray(response?.data?.results) ? response.data.results[0] : null;
        if (!first) return null;
        return {
          address: first.address || raw,
          lat: Number(first.lat),
          lng: Number(first.lng),
        };
      } catch (_) {
        return mode === "address" ? { address: raw, lat: null, lng: null } : null;
      }
    },
    []
  );

  useEffect(() => {
    const route = initialRoute || null;
    if (!route) {
      setSourceText("");
      setDestinationText("");
      setRouteName("");
      setStops([]);
      setCurrentStop("");
      setCurrentStopResolved(null);
      setSelectedDeviceId("");
      setDirectionsResponse(null);
      setShouldRenderRoute(false);
      setSelectedRouteIndex(0);
      setRouteRequestKey(0);
      setIsRouteLoading(false);
      lastDirectionsErrorRef.current = "";
      return;
    }

    setSourceText(String(route?.source?.address || route?.sourceAddress || route?.source || ""));
    setDestinationText(String(route?.destination?.address || route?.destinationAddress || route?.destination || ""));
    setRouteName(String(route?.routeName || route?.name || ""));
    setStops(
      normalizeStops(route?.stops).map((s) => ({
        location: s.address,
        lat: s.lat,
        lng: s.lng,
        stopover: true,
      }))
    );
    setCurrentStop("");
    setCurrentStopResolved(null);
    setSelectedDeviceId(
      normalizeMongoId(route?.assignedDevice?._id || route?.assignedDevice || route?.deviceId || "")
    );
    setDirectionsResponse(null);
    setIsRouteLoading(true);
    setShouldRenderRoute(true);
    setSelectedRouteIndex(0);
    setRouteRequestKey((prev) => prev + 1);
    lastDirectionsErrorRef.current = "";
  }, [initialRoute]);

  const onSourceChanged = () => {
    const place = sourceRef.current?.getPlace?.();
    if (!place?.formatted_address) return;
    setSourceText(place.formatted_address);
    setDirectionsResponse(null);
    setShouldRenderRoute(false);
    setIsRouteLoading(false);
    lastDirectionsErrorRef.current = "";
  };

  const onDestChanged = () => {
    const place = destRef.current?.getPlace?.();
    if (!place?.formatted_address) return;
    setDestinationText(place.formatted_address);
    setDirectionsResponse(null);
    setShouldRenderRoute(false);
    setIsRouteLoading(false);
    lastDirectionsErrorRef.current = "";
  };

  const onStopChanged = () => {
    const place = stopRef.current?.getPlace?.();
    if (!place?.formatted_address) return;
    setCurrentStop(place.formatted_address);
    setCurrentStopResolved({
      address: place.formatted_address,
      lat:
        typeof place?.geometry?.location?.lat === "function"
          ? place.geometry.location.lat()
          : null,
      lng:
        typeof place?.geometry?.location?.lng === "function"
          ? place.geometry.location.lng()
          : null,
    });
  };

  const addStop = async (event) => {
    event.preventDefault();
    if (!currentStop.trim()) return;

    const resolvedStop =
      currentStopResolved?.address && currentStopResolved.address === currentStop.trim()
        ? currentStopResolved
        : await resolveTypedLocation(currentStop, "stop");
    const nextStop = {
      location: resolvedStop?.address || currentStop.trim(),
      lat: Number.isFinite(Number(resolvedStop?.lat)) ? Number(resolvedStop.lat) : null,
      lng: Number.isFinite(Number(resolvedStop?.lng)) ? Number(resolvedStop.lng) : null,
      stopover: true,
    };

    setStops((prev) => [...prev, nextStop]);
    setCurrentStop("");
    setCurrentStopResolved(null);
    setSelectedRouteIndex(0);
    lastDirectionsErrorRef.current = "";
    if (sourceText.trim() && destinationText.trim()) {
      setIsRouteLoading(true);
      setRouteRequestKey((prev) => prev + 1);
      setShouldRenderRoute(true);
    } else {
      setShouldRenderRoute(false);
      setIsRouteLoading(false);
    }
    const input = document.getElementById("route-stop-input");
    if (input) input.value = "";
  };

  const removeStop = (index) => {
    setStops((prev) => prev.filter((_, idx) => idx !== index));
    setSelectedRouteIndex(0);
    lastDirectionsErrorRef.current = "";
    if (sourceText.trim() && destinationText.trim()) {
      setIsRouteLoading(true);
      setRouteRequestKey((prev) => prev + 1);
      setShouldRenderRoute(true);
    } else {
      setShouldRenderRoute(false);
      setIsRouteLoading(false);
    }
  };

  const moveStop = (index, direction) => {
    setStops((prev) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
    setSelectedRouteIndex(0);
    lastDirectionsErrorRef.current = "";
    if (sourceText.trim() && destinationText.trim()) {
      setIsRouteLoading(true);
      setRouteRequestKey((prev) => prev + 1);
      setShouldRenderRoute(true);
    }
  };

  const directionsCallback = useCallback((response) => {
    const status = String(response?.status || "");
    if (response?.status === "OK") {
      setIsRouteLoading(false);
      setShouldRenderRoute(false);
      setDirectionsResponse(response);
      lastDirectionsErrorRef.current = "";
      setSelectedRouteIndex((prev) => {
        const totalRoutes = Array.isArray(response?.routes) ? response.routes.length : 0;
        if (!totalRoutes) return 0;
        return prev >= totalRoutes ? 0 : prev;
      });
      return;
    }

    if (!status) return;

    setIsRouteLoading(false);
    setShouldRenderRoute(false);
    setDirectionsResponse(null);
    if (lastDirectionsErrorRef.current !== status) {
      lastDirectionsErrorRef.current = status;
      showStatus("error", getDirectionsErrorMessage(status));
    }
  }, [showStatus]);

  const selectedRoute = useMemo(() => {
    const routes = Array.isArray(directionsResponse?.routes) ? directionsResponse.routes : [];
    return routes[selectedRouteIndex] || routes[0] || null;
  }, [directionsResponse, selectedRouteIndex]);

  const routeOptions = useMemo(() => {
    const routes = Array.isArray(directionsResponse?.routes) ? directionsResponse.routes : [];
    return routes.map((route, index) => {
      const legs = Array.isArray(route?.legs) ? route.legs : [];
      const meters = legs.reduce((acc, leg) => acc + (leg?.distance?.value || 0), 0);
      const seconds = legs.reduce((acc, leg) => acc + (leg?.duration?.value || 0), 0);
      const summary = String(route?.summary || "").trim();
      return {
        index,
        summary: summary || `Route ${index + 1}`,
        distanceKm: meters ? meters / 1000 : 0,
        durationMin: seconds ? seconds / 60 : 0,
      };
    });
  }, [directionsResponse]);

  const distanceKm = useMemo(() => {
    const legs = selectedRoute?.legs || [];
    const meters = legs.reduce((acc, leg) => acc + (leg?.distance?.value || 0), 0);
    return meters ? meters / 1000 : 0;
  }, [selectedRoute]);

  const durationMin = useMemo(() => {
    const legs = selectedRoute?.legs || [];
    const seconds = legs.reduce((acc, leg) => acc + (leg?.duration?.value || 0), 0);
    return seconds ? seconds / 60 : 0;
  }, [selectedRoute]);

  const directionsWaypoints = useMemo(() => {
    return (Array.isArray(stops) ? stops : [])
      .map((stop) => {
        const lat = Number(stop?.lat);
        const lng = Number(stop?.lng);
        const address = String(stop?.location || "").trim();
        const location = Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : address;
        if (!location) return null;
        return { location, stopover: true };
      })
      .filter(Boolean);
  }, [stops]);

  const routeRenderKey = useMemo(() => {
    const waypointKey = directionsWaypoints
      .map((stop) =>
        typeof stop.location === "string"
          ? stop.location
          : `${stop.location.lat},${stop.location.lng}`
      )
      .join("|");
    return `${routeRequestKey}:${sourceText}:${destinationText}:${waypointKey}`;
  }, [destinationText, directionsWaypoints, routeRequestKey, sourceText]);

  const submit = async () => {
    if (!routeName.trim() || !sourceText.trim() || !destinationText.trim()) {
      showStatus("warning", "Source, Destination, Route Name required hain.");
      return;
    }

    const [resolvedSource, resolvedDestination, resolvedStops] = await Promise.all([
      resolveTypedLocation(sourceText, "address"),
      resolveTypedLocation(destinationText, "address"),
      Promise.all(
        stops.map(async (stop) => {
          const resolved = await resolveTypedLocation(stop.location, "stop");
          return {
            address: resolved?.address || stop.location,
            lat: Number.isFinite(Number(resolved?.lat)) ? Number(resolved.lat) : null,
            lng: Number.isFinite(Number(resolved?.lng)) ? Number(resolved.lng) : null,
            stopOrder: 0,
          };
        })
      ),
    ]);

    if (!resolvedSource || !resolvedDestination) {
      showStatus("error", "Location search failed. Place name ya lat,lng dobara try karo.");
      return;
    }

    const selectedRoadPathFromMap = extractSelectedRoutePath(selectedRoute);
    const existingRoadPath = normalizeExistingRoutePath(
      initialRoute?.routePath || initialRoute?.path || initialRoute?.polyline
    );
    const selectedRoadPath =
      selectedRoadPathFromMap.length >= 2 ? selectedRoadPathFromMap : existingRoadPath;

    const routePayload = {
      routeName: routeName.trim(),
      source: {
        address: resolvedSource.address || sourceText.trim(),
        lat: Number.isFinite(Number(resolvedSource.lat)) ? Number(resolvedSource.lat) : null,
        lng: Number.isFinite(Number(resolvedSource.lng)) ? Number(resolvedSource.lng) : null,
      },
      destination: {
        address: resolvedDestination.address || destinationText.trim(),
        lat: Number.isFinite(Number(resolvedDestination.lat)) ? Number(resolvedDestination.lat) : null,
        lng: Number.isFinite(Number(resolvedDestination.lng)) ? Number(resolvedDestination.lng) : null,
      },
      stops: resolvedStops.map((stop, idx) => ({ ...stop, stopOrder: idx + 1 })),
      routePath: selectedRoadPath,
      selectedPathSummary: String(selectedRoute?.summary || initialRoute?.selectedPathSummary || "").trim(),
      totalDistance: distanceKm ? String(distanceKm.toFixed(2)) : String(kmFromTotalDistance(initialRoute?.totalDistance || 0) || 0),
      totalDuration: durationMin ? String(Math.round(durationMin)) : String(initialRoute?.totalDuration || ""),
    };

    let saved = null;
    try {
      if (isEdit) {
        const id = initialRoute?._id || initialRoute?.id;
        saved = await updateRoute({ id, ...routePayload }).unwrap();
      } else {
        saved = await addRoute(routePayload).unwrap();
      }
    } catch (error) {
      showStatus("error", error?.data?.error || error?.data?.msg || "Route save failed.");
      return;
    }

    const routeId =
      saved?.data?._id ||
      saved?._id ||
      saved?.route?._id ||
      saved?.data?.id ||
      initialRoute?._id ||
      null;

    let assignWarning = "";
    if (selectedDeviceId && routeId) {
      try {
        await updateAdvancedSettings({ deviceId: selectedDeviceId, defaultRouteId: routeId }).unwrap();
      } catch (error) {
        assignWarning = error?.data?.error || error?.data?.msg || "Route saved, but device assign failed.";
      }
    }

    showStatus(
      assignWarning ? "warning" : "success",
      assignWarning || (isEdit ? "Route updated." : "Route created.")
    );

    try {
      await onSaved?.();
    } catch (_) {
      // UI refresh/close should not turn a successful save into a failure toast.
    }
  };

  const busy = adding || updating || assigning;

  if (!isLoaded) return <Loader label="Loading Map..." minHeight="420px" background="#fff" />;

  return (
    <Box sx={{ px: compact ? 0 : 2.5, py: compact ? 0 : 2 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(320px, 30%) minmax(0, 1fr)" }, gap: 2, alignItems: "stretch" }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: "22px",
            border: "1px solid rgba(226,232,240,0.95)",
            boxShadow: "0 18px 42px rgba(15,23,42,0.06)",
            bgcolor: "#fff",
          }}
        >
          <Typography sx={{ mb: 1, fontWeight: 800 }}>Route Name *</Typography>
            <TextField
              fullWidth
              placeholder="Enter route name"
              value={routeName}
              onChange={(e) => setRouteName(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: "#fff",
                  boxShadow: "0 10px 24px rgba(15,23,42,0.04)",
                  "& fieldset": { borderColor: "#dbe3ef" },
                },
              }}
              disabled={isView}
            />

          <Typography sx={{ mt: 2, mb: 1, fontWeight: 800 }}>Source *</Typography>
          <Autocomplete onLoad={(ref) => (sourceRef.current = ref)} onPlaceChanged={onSourceChanged}>
            <input
              className="ls-rt-input"
              style={{ width: "100%", borderRadius: 14, border: "1px solid #dbe3ef", padding: "14px 14px", boxShadow: "0 10px 24px rgba(15,23,42,0.04)" }}
              placeholder="Enter a location"
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              disabled={isView}
            />
          </Autocomplete>

          <Typography sx={{ mt: 2, mb: 1, fontWeight: 800 }}>Destination *</Typography>
          <Autocomplete onLoad={(ref) => (destRef.current = ref)} onPlaceChanged={onDestChanged}>
            <input
              className="ls-rt-input"
              style={{ width: "100%", borderRadius: 14, border: "1px solid #dbe3ef", padding: "14px 14px", boxShadow: "0 10px 24px rgba(15,23,42,0.04)" }}
              placeholder="Enter a location"
              value={destinationText}
              onChange={(e) => setDestinationText(e.target.value)}
              disabled={isView}
            />
          </Autocomplete>

          <Typography sx={{ mt: 2, mb: 1, fontWeight: 800 }}>Add Address</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 1, alignItems: "center" }}>
            <Autocomplete onLoad={(ref) => (stopRef.current = ref)} onPlaceChanged={onStopChanged}>
              <input
                id="route-stop-input"
                style={{ width: "100%", borderRadius: 14, border: "1px solid #dbe3ef", padding: "14px 14px", boxShadow: "0 10px 24px rgba(15,23,42,0.04)" }}
                placeholder="Stop:"
                value={currentStop}
                onChange={(e) => {
                  setCurrentStop(e.target.value);
                  setCurrentStopResolved(null);
                }}
                disabled={isView}
              />
            </Autocomplete>
            <IconButton onClick={addStop} disabled={isView} sx={{ border: "1px solid #dbe3ef", borderRadius: 1.8, bgcolor: "#fff", boxShadow: "0 10px 24px rgba(15,23,42,0.04)" }}>
              <Plus size={18} />
            </IconButton>
            <IconButton onClick={() => setCurrentStop("")} disabled={isView} sx={{ color: "#ef4444" }}>
              <Trash2 size={18} />
            </IconButton>
          </Box>

          {stops.length ? (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {stops.map((s, idx) => (
                <Box key={`${idx}-${s.location}`} sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }} noWrap title={s.location}>
                    {idx + 1}. {s.location}
                  </Typography>
                  {!isView && (
                    <Stack direction="row" spacing={0.25}>
                      <IconButton size="small" onClick={() => moveStop(idx, -1)} disabled={idx === 0} sx={{ color: "#64748b" }}>
                        ↑
                      </IconButton>
                      <IconButton size="small" onClick={() => moveStop(idx, 1)} disabled={idx === stops.length - 1} sx={{ color: "#64748b" }}>
                        ↓
                      </IconButton>
                      <IconButton size="small" onClick={() => removeStop(idx)} sx={{ color: "#ef4444" }}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Stack>
                  )}
                </Box>
              ))}
            </Stack>
          ) : null}

          <Typography sx={{ mt: 2, mb: 1, fontWeight: 800 }}>Assign Device (Optional)</Typography>
          <FormControl fullWidth>
            <Select
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
              sx={{
                borderRadius: "16px",
                backgroundColor: "#fff",
                boxShadow: "0 10px 24px rgba(15,23,42,0.04)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ef" },
              }}
              disabled={isView}
            >
              <MenuItem value="">None</MenuItem>
              {devices.map((d) => (
                <MenuItem key={d._id} value={d._id}>
                  {d?.name || d?.imei || "Device"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={() => {
                if (!sourceText.trim() || !destinationText.trim()) {
                  showStatus("warning", "Source aur destination select karo.");
                  return;
                }
                setDirectionsResponse(null);
                setSelectedRouteIndex(0);
                setIsRouteLoading(true);
                lastDirectionsErrorRef.current = "";
                setRouteRequestKey((prev) => prev + 1);
                setShouldRenderRoute(true);
              }}
              disabled={!sourceText.trim() || !destinationText.trim()}
              sx={{ bgcolor: "#fbbf24", color: "#111827", fontWeight: 900, borderRadius: 2.5, "&:hover": { bgcolor: "#f59e0b" } }}
            >
              {isRouteLoading ? "Loading Route..." : "Show Route"}
            </Button>
          </Stack>

          {routeOptions.length > 1 ? (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 800, color: "#111827" }}>Select Path</Typography>
              {routeOptions.map((route) => (
                <Box
                  key={route.index}
                  onClick={() => setSelectedRouteIndex(route.index)}
                  sx={{
                    border: "1px solid",
                    borderColor: route.index === selectedRouteIndex ? "#2563eb" : "#e5e7eb",
                    bgcolor: route.index === selectedRouteIndex ? "#eff6ff" : "#fff",
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1.25,
                    cursor: "pointer",
                  }}
                >
                  <Typography sx={{ fontWeight: 800, color: "#111827" }}>
                    {route.summary}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                    {route.distanceKm.toFixed(2)} km • {Math.round(route.durationMin)} mins
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : null}

          <Stack direction="row" spacing={1.25} sx={{ mt: 2.5 }}>
            {!isView ? (
              <Button
                fullWidth
                variant="contained"
                onClick={submit}
                disabled={busy}
                sx={{
                  bgcolor: "#1d63c8",
                  "&:hover": { bgcolor: "#114fae" },
                  fontWeight: 900,
                  borderRadius: 2,
                  minHeight: 46,
                  boxShadow: "0 16px 30px rgba(29,99,200,0.24)",
                }}
              >
                {busy ? `${submitLabel}...` : submitLabel}
              </Button>
            ) : null}
            <Button
              fullWidth
              variant="outlined"
              onClick={onCancel}
              disabled={busy}
              sx={{
                borderColor: "#cbd5e1",
                color: "#334155",
                "&:hover": { borderColor: "#94a3b8", background: "#f8fafc" },
                fontWeight: 900,
                borderRadius: 2,
                minHeight: 46,
              }}
            >
              {cancelLabel}
            </Button>
          </Stack>
        </Paper>

        <Box sx={{ borderRadius: "22px", overflow: "hidden", border: "1px solid #e5e7eb", boxShadow: "0 18px 42px rgba(15,23,42,0.06)", minHeight: 560 }}>
          <GoogleMap mapContainerStyle={mapContainerStyle} center={defaultCenter} zoom={2}>
            {shouldRenderRoute && sourceText && destinationText ? (
              <DirectionsService
                key={`route-${routeRenderKey}`}
                options={{
                  origin: sourceText,
                  destination: destinationText,
                  waypoints: directionsWaypoints,
                  optimizeWaypoints: true,
                  provideRouteAlternatives: true,
                  travelMode: "DRIVING",
                }}
                callback={directionsCallback}
              />
            ) : null}
            {directionsResponse ? <DirectionsRenderer directions={directionsResponse} options={{ routeIndex: selectedRouteIndex }} /> : null}
          </GoogleMap>
        </Box>
      </Box>

    </Box>
  );
}

export default function RoutePortalModal({ open = true, onClose, asPage = false, pageMode = "list" }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showStatus } = useStatus();
  const routesQ = useGetRoutesQuery(undefined, { skip: !asPage && !open });
  const { data: devicesPayload = [] } = useGetAllLiteDevicesQuery();
  const [deleteRoute, { isLoading: isDeleting }] = useDeleteRouteMutation();
  const [updateAdvancedSettings, { isLoading: isAssigning }] = useUpdateDeviceAdvancedSettingsMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState("table"); // table | create | edit | view
  const [activeRoute, setActiveRoute] = useState(null);
  const [assignRoute, setAssignRoute] = useState(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [pendingAssignIds, setPendingAssignIds] = useState(() => new Set());
  const [assignOverrides, setAssignOverrides] = useState(() => new Map()); // deviceId -> boolean
  const [activeTab, setActiveTab] = useState("list");

  const devices = useMemo(
    () => (Array.isArray(devicesPayload) ? devicesPayload : []).filter((d) => d && (d._id || d.id)),
    [devicesPayload]
  );

  const routes = useMemo(() => parseRoutesPayload(routesQ.data), [routesQ.data]);

  const normalizedRoutes = useMemo(() => {
    return routes.map((route, idx) => {
      const id = route?._id || route?.id;
      const status = route?.status === false || String(route?.status || "").toLowerCase() === "inactive" ? "Inactive" : "Active";
      return {
        raw: route,
        id,
        idLabel: route?.id || (id ? String(id).slice(-4) : String(idx + 1)),
        routeName: route?.routeName || "-",
        source: route?.source?.address || "-",
        destination: route?.destination?.address || "-",
        stopsCount: Array.isArray(route?.stops) ? route.stops.length : 0,
        routeLength: kmFromTotalDistance(route?.totalDistance).toFixed(2),
        routeLengthValue: kmFromTotalDistance(route?.totalDistance),
        assignedVehicle:
          route?.assignedDevice?.name ||
          route?.assignedDevice?.imei ||
          route?.device?.name ||
          route?.vehicle?.name ||
          "-",
        createdDate: formatTimestamp(route?.createdAt || route?.createdOn || route?.updatedAt) || "-",
        status,
      };
    });
  }, [routes]);

  const filteredRoutes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return normalizedRoutes;
    return normalizedRoutes.filter((row) => {
      return [row.routeName, row.source, row.destination].some((v) => String(v || "").toLowerCase().includes(query));
    });
  }, [normalizedRoutes, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredRoutes.length / rowsPerPage));
  const pagedRoutes = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRoutes.slice(start, start + rowsPerPage);
  }, [filteredRoutes, page, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const summary = useMemo(() => {
    const total = normalizedRoutes.length;
    const active = normalizedRoutes.filter((route) => route.status === "Active").length;
    const inactive = Math.max(0, total - active);
    const totalDistance = normalizedRoutes.reduce((sum, route) => sum + (Number(route.routeLengthValue) || 0), 0);
    return { total, active, inactive, totalDistance };
  }, [normalizedRoutes]);

  const summaryCards = useMemo(
    () => [
      {
        key: "total",
        label: "Total Routes",
        value: summary.total,
        detail: "All time",
        tone: "neutral",
        icon: <RouteOutlinedIcon fontSize="small" />,
      },
      {
        key: "active",
        label: "Active Routes",
        value: summary.active,
        detail: "Currently active",
        tone: "success",
        icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />,
      },
      {
        key: "inactive",
        label: "Inactive Routes",
        value: summary.inactive,
        detail: "Not running",
        tone: "warning",
        icon: <ScheduleOutlinedIcon fontSize="small" />,
      },
      {
        key: "distance",
        label: "Total Distance",
        value: `${summary.totalDistance.toFixed(2)} km`,
        detail: "All routes",
        tone: "violet",
        icon: <StraightenOutlinedIcon fontSize="small" />,
      },
    ],
    [summary]
  );

  const handleDownloadCsv = () => {
    const csv = buildCsv(filteredRoutes);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "routes.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Routes CSV downloaded.");
  };

  const handleDelete = async (row) => {
    const id = row?.id;
    if (!id) return;
    if (!window.confirm("Route delete karna hai?")) return;
    try {
      await deleteRoute(id).unwrap();
      showStatus("success", "Route deleted.");
      await routesQ.refetch?.();
    } catch (error) {
      showStatus("error", error?.data?.error || "Failed to delete route.");
    }
  };

  const closeForm = () => {
    setActiveRoute(null);
    setMode("table");
  };

  const busy = isDeleting || isAssigning;

  const assignDevices = useMemo(() => {
    const query = String(assignSearch || "").trim().toLowerCase();
    const routeId = String(assignRoute?.id || "");

    const list = devices
      .map((device) => {
        const id = device?._id || device?.id;
        const status = getDeviceStatus(device);
        const lastUpdate = formatTimestamp(device?.lastDataReceivedAt || device?.liveData?.timestamp);
        const assignedRouteId = normalizeMongoId(device?.advancedSettings?.defaultRouteId);
        const baseChecked = routeId && String(assignedRouteId || "") === routeId;
        const overrideChecked = assignOverrides.get(String(id));
        const checked = typeof overrideChecked === "boolean" ? overrideChecked : baseChecked;
        return {
          raw: device,
          id,
          name: device?.name || device?.vehicleNumber || device?.imei || "-",
          imei: device?.imei || "-",
          status,
          lastUpdate,
          simNo: device?.msdn || "-",
          model: device?.model || "-",
          icon: device?.vehicleType || device?.deviceIcon || "Default",
          driverName: device?.driver?.driverName || "-",
          driverContact: device?.driver?.contactNumber || "-",
          checked,
        };
      })
      .filter((row) => row.id);

    if (!query) return list;
    return list.filter((row) => {
      return [row.name, row.imei, row.status, row.simNo, row.model, row.icon, row.driverName, row.driverContact]
        .some((v) => String(v || "").toLowerCase().includes(query));
    });
  }, [assignOverrides, assignRoute?.id, assignSearch, devices]);

  const toggleAssign = async (deviceRow, nextChecked) => {
    const routeId = String(assignRoute?.id || "");
    const deviceId = deviceRow?.id;
    if (!routeId || !deviceId) return;

    setPendingAssignIds((prev) => new Set(prev).add(String(deviceId)));
    try {
      await updateAdvancedSettings({
        deviceId,
        defaultRouteId: nextChecked ? routeId : null,
      }).unwrap();
      setAssignOverrides((prev) => {
        const next = new Map(prev);
        next.set(String(deviceId), Boolean(nextChecked));
        return next;
      });
      showStatus("success", nextChecked ? "Assigned." : "Unassigned.");
    } catch (error) {
      showStatus("error", error?.data?.msg || error?.data?.error || "Assign failed.");
    } finally {
      setPendingAssignIds((prev) => {
        const next = new Set(prev);
        next.delete(String(deviceId));
        return next;
      });
    }
  };

  const handleStartCreate = () => {
    if (asPage && pageMode !== "create") {
      window.location.assign("/routes/create");
      return;
    }
    setMode("create");
    setActiveRoute(null);
    const formNode = document.getElementById("route-form-card");
    if (formNode) formNode.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const listCard = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        border: "1px solid rgba(226,232,240,0.92)",
        boxShadow: "0 18px 42px rgba(15,23,42,0.05)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ borderBottom: "1px solid rgba(226,232,240,0.92)" }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ px: 2.2 }}>
          <Tab value="list" label="Routes List" sx={{ textTransform: "none", fontWeight: 800 }} />
          <Tab value="map" label="Map View" sx={{ textTransform: "none", fontWeight: 800 }} />
        </Tabs>
      </Box>

      {activeTab === "list" ? (
        <>
          <Box sx={{ p: { xs: 1.5, md: 2.2 }, borderBottom: "1px solid rgba(226,232,240,0.88)" }}>
            <Stack direction={{ xs: "column", xl: "row" }} spacing={1.5} justifyContent="space-between">
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ flex: 1 }}>
                <TextField
                  placeholder="Search by route name, source or destination..."
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setPage(1);
                  }}
                  sx={{
                    minWidth: { xs: "100%", md: 370 },
                    "& .MuiOutlinedInput-root": {
                      height: 50,
                      borderRadius: "16px",
                      bgcolor: "#fff",
                      boxShadow: "0 10px 26px rgba(15,23,42,0.03)",
                      "& fieldset": { borderColor: "rgba(226,232,240,0.95)" },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon style={{ color: "#64748b" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  startIcon={<FilterListRoundedIcon />}
                  sx={{
                    minWidth: 110,
                    height: 50,
                    borderRadius: "16px",
                    border: "1px solid rgba(226,232,240,0.95)",
                    bgcolor: "#fff",
                    color: "#334155",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "0 10px 26px rgba(15,23,42,0.03)",
                  }}
                >
                  Filters
                </Button>
              </Stack>

              <Stack direction="row" spacing={1.1} justifyContent="flex-end">
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={() => routesQ.refetch?.()}
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "16px",
                      border: "1px solid rgba(226,232,240,0.95)",
                      bgcolor: "#fff",
                      boxShadow: "0 10px 26px rgba(15,23,42,0.03)",
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download CSV">
                  <IconButton
                    onClick={handleDownloadCsv}
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "16px",
                      border: "1px solid rgba(226,232,240,0.95)",
                      bgcolor: "#fff",
                      boxShadow: "0 10px 26px rgba(15,23,42,0.03)",
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <FormControl sx={{ minWidth: 92 }}>
                  <Select
                    value={rowsPerPage}
                    onChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(1);
                    }}
                    sx={{
                      height: 50,
                      borderRadius: "16px",
                      bgcolor: "#fff",
                      boxShadow: "0 10px 26px rgba(15,23,42,0.03)",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(226,232,240,0.95)" },
                    }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <MenuItem key={size} value={size}>{size}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Box>

          {!isMobile ? (
            <TableContainer sx={{ px: 2, pt: 2, minHeight: 420 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Id", "Route Name", "Source", "Destination", "Stops", "Distance", "Assigned Vehicle", "Status", "Created Date", "Actions"].map((head) => (
                      <TableCell key={head} sx={{ borderBottom: "1px solid rgba(226,232,240,0.9)", color: "#64748b", fontSize: "0.85rem", fontWeight: 700, py: 2.2 }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRoutes.length ? (
                    pagedRoutes.map((row) => (
                      <TableRow key={row.id} hover sx={{ "& td": { py: 2.2, borderBottom: "1px solid rgba(241,245,249,1)" } }}>
                        <TableCell sx={{ fontWeight: 700 }}>{row.idLabel}</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: "#0f172a" }}>{row.routeName}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#334155", maxWidth: 240 }}>{row.source}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#334155", maxWidth: 240 }}>{row.destination}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{row.stopsCount}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{row.routeLength} km</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{row.assignedVehicle}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            sx={{
                              borderRadius: "999px",
                              bgcolor: row.status === "Active" ? "rgba(34,197,94,0.12)" : "rgba(249,115,22,0.12)",
                              color: row.status === "Active" ? "#16a34a" : "#ea580c",
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{row.createdDate}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Edit Route">
                              <IconButton size="small" onClick={() => { setActiveRoute(row.raw); setMode("edit"); }} sx={{ width: 38, height: 38, bgcolor: "#2563eb", borderRadius: "12px" }}>
                                <EditOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Route">
                              <IconButton size="small" onClick={() => { setActiveRoute(row.raw); setMode("view"); setActiveTab("map"); }} sx={{ width: 38, height: 38, bgcolor: "#2563eb", borderRadius: "12px" }}>
                                <VisibilityOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Assign Vehicle">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setAssignRoute(row);
                                  setAssignSearch("");
                                  setPendingAssignIds(new Set());
                                  setAssignOverrides(new Map());
                                }}
                                sx={{ width: 38, height: 38, bgcolor: "#2563eb", borderRadius: "12px" }}
                              >
                                <DirectionsCarFilledOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Route">
                              <IconButton size="small" onClick={() => handleDelete(row)} sx={{ width: 38, height: 38, bgcolor: "#ef4444", borderRadius: "12px" }} disabled={busy}>
                                <DeleteOutlineIcon sx={{ color: "#fff", fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Stack spacing={1.25} alignItems="center" justifyContent="center" sx={{ py: 9 }}>
                          <Box sx={{ width: 96, height: 96, borderRadius: "50%", bgcolor: "rgba(59,130,246,0.08)", color: "#2563eb", display: "grid", placeItems: "center" }}>
                            <RouteOutlinedIcon sx={{ fontSize: 40 }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", color: "#0f172a" }}>No routes found</Typography>
                          <Typography sx={{ color: "#64748b" }}>Create your first route to get started.</Typography>
                          <Button variant="contained" startIcon={<AddIcon />} onClick={handleStartCreate} sx={{ borderRadius: "14px", mt: 1, textTransform: "none", fontWeight: 700 }}>
                            Add Route
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 1.5, display: "grid", gap: 1.25 }}>
              {pagedRoutes.length ? (
                pagedRoutes.map((row) => (
                  <Paper key={row.id} elevation={0} sx={{ p: 1.5, borderRadius: "18px", border: "1px solid rgba(226,232,240,0.95)", boxShadow: "0 12px 26px rgba(15,23,42,0.04)" }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>{row.routeName}</Typography>
                        <Chip label={row.status} size="small" sx={{ borderRadius: "999px", bgcolor: row.status === "Active" ? "rgba(34,197,94,0.12)" : "rgba(249,115,22,0.12)", color: row.status === "Active" ? "#16a34a" : "#ea580c", fontWeight: 700 }} />
                      </Stack>
                      <Typography sx={{ color: "#64748b", fontSize: "0.86rem" }}>Source: {row.source}</Typography>
                      <Typography sx={{ color: "#64748b", fontSize: "0.86rem" }}>Destination: {row.destination}</Typography>
                      <Typography sx={{ color: "#64748b", fontSize: "0.86rem" }}>Stops: {row.stopsCount}</Typography>
                      <Typography sx={{ color: "#64748b", fontSize: "0.86rem" }}>Distance: {row.routeLength} km</Typography>
                      <Stack direction="row" spacing={1}>
                        <IconButton size="small" onClick={() => { setActiveRoute(row.raw); setMode("edit"); }} sx={{ width: 38, height: 38, bgcolor: "#2563eb", borderRadius: "12px" }}><EditOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} /></IconButton>
                        <IconButton size="small" onClick={() => { setActiveRoute(row.raw); setMode("view"); setActiveTab("map"); }} sx={{ width: 38, height: 38, bgcolor: "#2563eb", borderRadius: "12px" }}><VisibilityOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} /></IconButton>
                        <IconButton size="small" onClick={() => handleDelete(row)} sx={{ width: 38, height: 38, bgcolor: "#ef4444", borderRadius: "12px" }}><DeleteOutlineIcon sx={{ color: "#fff", fontSize: 18 }} /></IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                ))
              ) : (
                <Paper sx={{ py: 7, textAlign: "center", borderRadius: 3 }}>
                  <Stack spacing={1.25} alignItems="center">
                    <Box sx={{ width: 88, height: 88, borderRadius: "50%", bgcolor: "rgba(59,130,246,0.08)", color: "#2563eb", display: "grid", placeItems: "center" }}>
                      <RouteOutlinedIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#0f172a" }}>No routes found</Typography>
                    <Typography sx={{ color: "#64748b", px: 2 }}>Create your first route to get started.</Typography>
                  </Stack>
                </Paper>
              )}
            </Box>
          )}

          <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap", borderTop: "1px solid rgba(226,232,240,0.88)" }}>
            <Typography sx={{ color: "#475569", fontSize: "0.92rem" }}>
              Showing {filteredRoutes.length ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredRoutes.length)} of {filteredRoutes.length} entries
            </Typography>
            <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} shape="rounded" />
          </Box>
        </>
      ) : (
        <Box sx={{ p: 2.2 }}>
          <Alert severity="info" sx={{ borderRadius: 2.5, mb: 2 }}>
            Select a route from the list actions to preview it on the map.
          </Alert>
          <RouteForm
            mode="view"
            initialRoute={activeRoute || routes[0] || null}
            devices={devices}
            onCancel={() => setActiveTab("list")}
            onSaved={async () => {}}
            cancelLabel="Back to List"
            compact
          />
        </Box>
      )}
    </Paper>
  );

  const pageContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
      {routesQ.isFetching ? (
        <Alert severity="info" sx={{ borderRadius: 2.5 }}>
          Refreshing routes...
        </Alert>
      ) : null}

      <FlexibleStatsRow items={summaryCards} />

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleStartCreate}
          sx={{
            height: 52,
            minWidth: 144,
            borderRadius: "16px",
            textTransform: "none",
            fontWeight: 700,
            bgcolor: "#1d63c8",
            boxShadow: "0 16px 30px rgba(29,99,200,0.24)",
            "&:hover": { bgcolor: "#114fae" },
          }}
        >
          Add Route
        </Button>
      </Stack>

      {listCard}

      <Paper
        id="route-form-card"
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2.2 },
          borderRadius: "24px",
          border: "1px solid rgba(226,232,240,0.92)",
          boxShadow: "0 18px 42px rgba(15,23,42,0.05)",
        }}
      >
        <Typography sx={{ fontSize: "1.7rem", fontWeight: 800, color: "#0f172a", mb: 2.2 }}>
          {mode === "edit" ? "Edit Route" : "Add New Route"}
        </Typography>
        <RouteForm
          mode={mode === "edit" ? "edit" : "create"}
          initialRoute={mode === "edit" ? activeRoute : null}
          devices={devices}
          onCancel={() => {
            closeForm();
            setMode("create");
          }}
          onSaved={async () => {
            await routesQ.refetch?.();
            setMode("create");
            setActiveRoute(null);
            setActiveTab("list");
          }}
          submitLabel={mode === "edit" ? "Update Route" : "Save Route"}
          cancelLabel="Reset"
          compact
        />
      </Paper>
    </Box>
  );

  const createPageContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: "24px",
          border: "1px solid rgba(226,232,240,0.92)",
          boxShadow: "0 18px 42px rgba(15,23,42,0.05)",
          bgcolor: "#fff",
          display: "flex",
          alignItems: { xs: "flex-start", lg: "center" },
          justifyContent: "space-between",
          gap: 2,
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        <Box>
          <Typography sx={{ fontSize: { xs: "1.8rem", md: "2.2rem" }, fontWeight: 900, color: "#0f172a" }}>
            Create New Route
          </Typography>
          <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: 15.5 }}>
            Select source, destination and stops to generate a route.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", lg: "auto" } }}>
          {["1 Select Source", "2 Select Stops", "3 Select Destination", "4 Save Route"].map((item) => (
            <Chip
              key={item}
              label={item}
              sx={{
                borderRadius: "999px",
                bgcolor: "rgba(37,99,235,0.08)",
                color: "#1d4ed8",
                fontWeight: 800,
              }}
            />
          ))}
        </Stack>
      </Paper>

      <RouteForm
        mode="create"
        initialRoute={null}
        devices={devices}
        onCancel={() => {
          setMode("create");
          setActiveRoute(null);
        }}
        onSaved={async () => {
          await routesQ.refetch?.();
          setMode("create");
          setActiveRoute(null);
        }}
        submitLabel="Save Route"
        cancelLabel="Reset"
        compact
      />
    </Box>
  );

  const listPageContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
      {routesQ.isFetching ? (
        <Alert severity="info" sx={{ borderRadius: 2.5 }}>
          Refreshing routes...
        </Alert>
      ) : null}

      <Box sx={{ display: "flex", alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
        <Box>
          <Typography sx={{ fontSize: { xs: "1.8rem", md: "2.2rem" }, fontWeight: 900, color: "#0f172a" }}>
            Route List
          </Typography>
          <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: 15.5 }}>
            Manage, assign and organize all saved routes.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => window.location.assign("/routes/create")}
          sx={{
            height: 48,
            minWidth: 170,
            borderRadius: "16px",
            textTransform: "none",
            fontWeight: 800,
            bgcolor: "#1d63c8",
            boxShadow: "0 16px 30px rgba(29,99,200,0.24)",
            "&:hover": { bgcolor: "#114fae" },
          }}
        >
          Create New Route
        </Button>
      </Box>

      <FlexibleStatsRow items={summaryCards} />

      {listCard}
    </Box>
  );

  return (
    <>
      {asPage ? (
        pageMode === "create" ? createPageContent : listPageContent
      ) : (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "80vh", background: "linear-gradient(180deg, #f7f9fd 0%, #f3f6fb 100%)" } }}>
          <DialogContent sx={{ p: 2.5 }}>
            {pageContent}
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={Boolean(assignRoute)} onClose={() => setAssignRoute(null)} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "72vh" } }}>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 900, fontSize: 18 }}>Assign Device</Typography>
            <IconButton onClick={() => setAssignRoute(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Selected Route */}
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography sx={{ fontWeight: 900, color: "#6b7280", mb: 1.25 }}>Selected Route</Typography>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid #e5e7eb" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Id", "Route Name", "Source", "Destination", "No of stops", "Route length"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 800, color: "#6b7280" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{assignRoute?.idLabel}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{assignRoute?.routeName}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignRoute?.source}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignRoute?.destination}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignRoute?.stopsCount}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignRoute?.routeLength}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Search */}
          <Box sx={{ px: 2.5, pb: 1.5 }}>
            <TextField
              placeholder="Search"
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
              sx={{ ...inputSx, width: "100%" }}
              InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
            />
          </Box>

          {/* Devices Table */}
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid #e5e7eb", maxHeight: "42vh" }}>
                <Table stickyHeader size="small" sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    {["Vehicle No", "Assigned Driver", "Driver Mobile", "IMEI", "Status", "Last Update", "Sim No", "Model", "Icon"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 800, color: "#111827" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignDevices.length ? (
                    assignDevices.map((row) => {
                      const pending = pendingAssignIds.has(String(row.id));
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={row.checked}
                              disabled={pending}
                              onChange={(e) => toggleAssign(row, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>{row.name}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.driverName}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.driverContact}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.imei}</TableCell>
                          <TableCell sx={{ fontWeight: 800, color: getStatusColor(row.status) }}>
                            {String(row.status || "").replace(/_/g, " ")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.lastUpdate}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.simNo}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.model}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.icon}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <EmptyState />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => setAssignRoute(null)}
                sx={{
                  minWidth: 220,
                  bgcolor: "#6b7280",
                  "&:hover": { bgcolor: "#4b5563" },
                  fontWeight: 900,
                  borderRadius: 1.2,
                }}
              >
                Close
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
